import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useBorrowerLoan } from "../../layouts/borrower/BorrowerLoanContext";
import { getBorrowerLoanMilestone } from "../../service/otpApi";

const MILESTONES = [
  { key: "application", label: "Application Received" },
  { key: "processing", label: "Processing" },
  { key: "underwriting", label: "Underwriting" },
  { key: "conditional", label: "Conditional Approval" },
  { key: "clearToClose", label: "Clear to Close" },
  { key: "closing", label: "Closing" },
  { key: "funded", label: "Funded" },
];

// Map Encompass milestone names to our milestone keys
function getMilestoneIndex(milestoneName) {
  if (!milestoneName) return -1;
  const lower = milestoneName.toLowerCase();

  if (lower.includes("fund")) return 6;
  if (lower.includes("closing") || lower.includes("close") && !lower.includes("clear")) return 5;
  if (lower.includes("clear to close") || lower.includes("ctc")) return 4;
  if (lower.includes("conditional") || lower.includes("cond")) return 3;
  if (lower.includes("underwrit")) return 2;
  if (lower.includes("process")) return 1;
  if (lower.includes("appli") || lower.includes("started") || lower.includes("submit")) return 0;
  return -1;
}

const BorrowerLoanTimeline = () => {
  const { loanId } = useBorrowerLoan();
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [milestoneDates, setMilestoneDates] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchMilestone = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await getBorrowerLoanMilestone(loanId);
        const data = res?.data || {};
        const idx = getMilestoneIndex(data.currentMilestone);
        setCurrentIndex(idx >= 0 ? idx : 0);
        setMilestoneDates(data.milestoneDates || {});
      } catch (err) {
        console.error("Error fetching milestone:", err);
        // Default to first step if we can't fetch
        setCurrentIndex(0);
        setError("Unable to fetch latest milestone status. Showing default.");
      } finally {
        setLoading(false);
      }
    };

    if (loanId) fetchMilestone();
  }, [loanId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 700, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Loan Status
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Track where your loan is in the process.
      </Typography>

      {error && (
        <Typography variant="caption" color="warning.main" sx={{ mb: 2, display: "block" }}>
          {error}
        </Typography>
      )}

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        {MILESTONES.map((ms, idx) => {
          const isCompleted = idx < currentIndex;
          const isActive = idx === currentIndex;
          const isPending = idx > currentIndex;

          return (
            <Box key={ms.key} sx={{ display: "flex", alignItems: "flex-start" }}>
              {/* Icon + connector line */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mr: 2,
                  minWidth: 32,
                }}
              >
                {isCompleted ? (
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
                ) : isActive ? (
                  <RadioButtonCheckedIcon
                    sx={{
                      color: "primary.main",
                      fontSize: 28,
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 1 },
                        "50%": { opacity: 0.5 },
                        "100%": { opacity: 1 },
                      },
                    }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: "grey.400", fontSize: 28 }} />
                )}
                {/* Connector line */}
                {idx < MILESTONES.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      height: 40,
                      bgcolor: isCompleted ? "success.main" : "grey.300",
                    }}
                  />
                )}
              </Box>

              {/* Label + date */}
              <Box sx={{ pt: 0.3, pb: idx < MILESTONES.length - 1 ? 2 : 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={isActive ? 700 : isCompleted ? 600 : 400}
                  color={isPending ? "text.disabled" : "text.primary"}
                >
                  {ms.label}
                </Typography>
                {milestoneDates[ms.key] && (
                  <Typography variant="caption" color="text.secondary">
                    {milestoneDates[ms.key]}
                  </Typography>
                )}
                {isActive && (
                  <Typography variant="caption" color="primary.main" display="block">
                    Current Step
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default BorrowerLoanTimeline;
